// Click handler for the "In current iteration" checkbox on task rows.
//
// POSTs to /iterations/add_task with task_id + include=0|1 and toggles
// the `inside` / `outside` CSS class based on the server's authoritative
// state. Visual changes optimistically on click and reverts on error.
//
// Wired by app/views/tasks/_task.html.haml; styles live in
// app/assets/tailwind/application.css under `.iteration-button`.

import { Controller } from "@hotwired/stimulus"

export default class extends Controller<HTMLElement> {
  async toggle(): Promise<void> {
    const taskId = this.element.dataset.taskId
    if (!taskId) return
    if (this.element.classList.contains("is-pending")) return

    const wasInside = this.element.classList.contains("inside")
    // Optimistic flip + lock against double-clicks.
    this.setState(!wasInside)
    this.element.classList.add("is-pending")

    const csrf =
      (document.querySelector("meta[name=csrf-token]") as HTMLMetaElement | null)?.content ?? ""
    const fd = new FormData()
    fd.append("task_id", taskId)
    fd.append("include", wasInside ? "0" : "1")
    fd.append("authenticity_token", csrf)

    try {
      const res = await fetch("/iterations/add_task", {
        method: "POST",
        body: fd,
        credentials: "same-origin",
        headers: { "X-Requested-With": "XMLHttpRequest", Accept: "application/json" },
      })
      const data = (await res.json()) as { success: boolean; state?: boolean; message?: string }
      if (!data.success) {
        this.setState(wasInside) // revert
        if (data.message) window.alert(data.message)
        return
      }
      // Server is authoritative — sync to its returned state.
      this.setState(!!data.state)
    } catch (e) {
      this.setState(wasInside) // revert on network failure
      window.alert("Could not update iteration. Please try again.")
    } finally {
      this.element.classList.remove("is-pending")
      this.updateTitle()
    }
  }

  private setState(inside: boolean): void {
    this.element.classList.toggle("inside", inside)
    this.element.classList.toggle("outside", !inside)
  }

  private updateTitle(): void {
    const inside = this.element.classList.contains("inside")
    this.element.title = inside
      ? "In current iteration — click to remove"
      : "Not in current iteration — click to add"
  }
}
